import { proctoringUBALogger } from './trackingUtil';

/**
 * Audio Proctoring Utilities
 * Handles audio capture, processing, and upload functionality
 */

/**
 * Get signed URL for audio upload
 * @param {string} token - Authentication token
 * @param {string} assessmentId - Assessment identifier
 * @param {string} incidentId - Voice incident identifier
 * @param {string} problemType - Type of problem
 * @returns {Promise<string>} Signed URL for upload
 */
export const getAudioSignedUrl = async (
  token,
  assessmentId,
  incidentId,
  problemType
) => {
  try {
    // This is a placeholder - implement based on your backend API
    const response = await fetch('/api/proctor/audio-signed-url', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        assessment_id: assessmentId,
        incident_id: incidentId,
        problem_type: problemType,
        timestamp: new Date().toISOString(),
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to get signed URL: ${response.status}`);
    }

    const data = await response.json();
    return data.signed_url;
  } catch (error) {
    proctoringUBALogger(
      'error',
      `Failed to get audio signed URL: ${error.message}`
    );
    throw error;
  }
};

/**
 * Upload audio blob to S3 using signed URL
 * @param {string} signedUrl - Pre-signed S3 URL
 * @param {Blob} audioBlob - Audio data to upload
 * @param {string} contentType - MIME type of audio
 * @returns {Promise<boolean>} Upload success status
 */
export const uploadAudioToS3 = async (
  signedUrl,
  audioBlob,
  contentType = 'audio/webm'
) => {
  try {
    const response = await fetch(signedUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: audioBlob,
    });

    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }

    proctoringUBALogger(
      'info',
      `Audio uploaded successfully to S3. Size: ${audioBlob.size} bytes`
    );
    return true;
  } catch (error) {
    proctoringUBALogger('error', `S3 upload failed: ${error.message}`);
    throw error;
  }
};

/**
 * Complete audio upload workflow
 * @param {Object} params - Upload parameters
 * @param {Blob} params.audioBlob - Audio data
 * @param {string} params.incidentId - Incident identifier
 * @param {string} params.token - Auth token
 * @param {string} params.assessmentId - Assessment ID
 * @param {string} params.problemType - Problem type
 * @returns {Promise<boolean>} Upload success status
 */
export const uploadAudioForProctoring = async ({
  audioBlob,
  incidentId,
  token,
  assessmentId,
  problemType,
}) => {
  try {
    // Step 1: Get signed URL
    const signedUrl = await getAudioSignedUrl(
      token,
      assessmentId,
      incidentId,
      problemType
    );

    // Step 2: Upload to S3
    await uploadAudioToS3(signedUrl, audioBlob);

    // Step 3: Log success
    proctoringUBALogger(
      'info',
      `Audio proctoring upload completed for incident ${incidentId}`
    );

    return true;
  } catch (error) {
    proctoringUBALogger(
      'error',
      `Audio proctoring upload failed for incident ${incidentId}: ${error.message}`
    );
    throw error;
  }
};

/**
 * Validate audio blob before upload
 * @param {Blob} audioBlob - Audio data to validate
 * @returns {Object} Validation result
 */
export const validateAudioBlob = audioBlob => {
  const result = {
    isValid: true,
    errors: [],
    warnings: [],
  };

  // Check if blob exists
  if (!audioBlob) {
    result.isValid = false;
    result.errors.push('Audio blob is missing');
    return result;
  }

  // Check blob size (max 10MB)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (audioBlob.size > maxSize) {
    result.isValid = false;
    result.errors.push(
      `Audio file too large: ${(audioBlob.size / 1024 / 1024).toFixed(2)}MB`
    );
  }

  // Check if blob is empty
  if (audioBlob.size === 0) {
    result.isValid = false;
    result.errors.push('Audio blob is empty');
  }

  // Check MIME type
  const allowedTypes = ['audio/webm', 'audio/wav', 'audio/mp3', 'audio/ogg'];
  if (!allowedTypes.includes(audioBlob.type)) {
    result.warnings.push(`Unsupported audio type: ${audioBlob.type}`);
  }

  return result;
};

/**
 * Convert AudioBuffer to Blob
 * @param {AudioBuffer} audioBuffer - Audio buffer to convert
 * @param {string} mimeType - Output MIME type
 * @returns {Promise<Blob>} Audio blob
 */
const audioBufferToBlob = async (audioBuffer, mimeType) => {
  return new Promise(resolve => {
    const mediaRecorder = new MediaRecorder(
      new MediaStream([
        new MediaStreamAudioSourceNode(new AudioContext(), {
          buffer: audioBuffer,
        }).mediaStreamTrack,
      ]),
      { mimeType }
    );

    const chunks = [];
    mediaRecorder.ondataavailable = event => chunks.push(event.data);
    mediaRecorder.onstop = () => resolve(new Blob(chunks, { type: mimeType }));

    mediaRecorder.start();
    setTimeout(() => mediaRecorder.stop(), 100);
  });
};

/**
 * Compress audio blob if needed
 * @param {Blob} audioBlob - Original audio data
 * @param {number} maxSize - Maximum size in bytes
 * @returns {Promise<Blob>} Compressed audio data
 */
export const compressAudioIfNeeded = async (
  audioBlob,
  maxSize = 5 * 1024 * 1024
) => {
  if (audioBlob.size <= maxSize) {
    return audioBlob;
  }

  try {
    // Create audio context for compression
    const audioContext = new (window.AudioContext ||
      window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Downsample to reduce size (simple approach)
    const offlineContext = new OfflineAudioContext(
      1, // mono
      audioBuffer.length * 0.5, // reduce duration
      22050 // lower sample rate
    );

    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;
    source.connect(offlineContext.destination);
    source.start();

    const renderedBuffer = await offlineContext.startRendering();
    const compressedBlob = await audioBufferToBlob(
      renderedBuffer,
      'audio/webm'
    );

    proctoringUBALogger(
      'info',
      `Audio compressed from ${audioBlob.size} to ${compressedBlob.size} bytes`
    );

    return compressedBlob;
  } catch (error) {
    proctoringUBALogger(
      'warning',
      `Audio compression failed: ${error.message}, using original`
    );
    return audioBlob;
  }
};

/**
 * Get audio metadata for logging
 * @param {Blob} audioBlob - Audio data
 * @param {number} duration - Audio duration in milliseconds (optional)
 * @returns {Object} Audio metadata
 */
export const getAudioMetadata = (audioBlob, duration = null) => {
  return {
    size: audioBlob.size,
    type: audioBlob.type,
    lastModified: audioBlob.lastModified,
    timestamp: new Date().toISOString(),
    duration, // Include duration if provided
  };
};

export default {
  getAudioSignedUrl,
  uploadAudioToS3,
  uploadAudioForProctoring,
  validateAudioBlob,
  compressAudioIfNeeded,
  getAudioMetadata,
};
